import type {
  FetchSalesPointListParams,
  SalesPoint
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  SALES_POINT = 'SalesPoint'
}

export const salesPointApi = createBaseApi({
  reducerPath: 'salesPointApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.SALES_POINT]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getSalesPoints: builder.query<ListResponse<SalesPoint>, FetchSalesPointListParams | void>({
      query: params => ({
        url: '/api/v1/sales-point/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ sales_point_id }) =>
                  ({ type: TagTypes.SALES_POINT, id: sales_point_id }) as const
              ),
              { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST }]
    }),
    getAllSalesPoints: builder.query<
      ListResponse<SalesPoint>,
      // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
      FetchSalesPointListParams | void
    >({
      query: (params = {}) => ({
        url: '/api/v1/sales-point/',
        params: {
          ...params,
          bypass_pagination: true
        }
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ sales_point_id }) =>
                  ({ type: TagTypes.SALES_POINT, id: sales_point_id }) as const
              ),
              { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST_ALL }
            ]
          : [{ type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST_ALL }]
    }),
    getSingleSalesPoint: builder.query<SalesPoint, string>({
      query: id => `/api/v1/sales-point/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.SALES_POINT, id }]
    }),
    createSingleSalesPoint: builder.mutation<SalesPoint, Partial<SalesPoint>>({
      query: data => ({
        url: `/api/v1/sales-point/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST },
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleSalesPoint: builder.mutation<SalesPoint, { id: string } & Partial<SalesPoint>>({
      query: data => ({
        url: `/api/v1/sales-point/${data.sales_point_id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.SALES_POINT, id },
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST },
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleSalesPoint: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/sales-point/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST },
        { type: TagTypes.SALES_POINT, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetSalesPointsQuery,
  useGetAllSalesPointsQuery,
  useGetSingleSalesPointQuery,
  useCreateSingleSalesPointMutation,
  useUpdateSingleSalesPointMutation,
  useDeleteSingleSalesPointMutation
} = salesPointApi
